import { isResource } from '@medplum/core'
import type { Coding } from '@medplum/fhirtypes'
import { codeableConcept, hasCodeableConcept } from '#src/codeableConcept.ts'
import { LIBRARY_TOPIC, NOTE_LABEL_TAG_SYSTEM } from '#src/codeSystems.ts'
import { Tag } from '#src/decorators.ts'
import { base64Decode, base64Encode } from '#src/encoding.ts'
import { ExtendedLibrary } from '#src/models/library.ts'
import {
  type NoteLabel,
  noteLabelTagCodings,
  readNoteLabelReferences,
} from '#src/models/noteLabel.ts'
import {
  mergeVisitNoteCategoryTokens,
  readVisitNoteCategoryTokens,
} from '#src/models/visitNoteCategory.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export class NoteTemplate extends ExtendedLibrary {
  constructor(data?: ConstructorParameters<typeof ExtendedLibrary>[0]) {
    super({
      ...data,
      topic: [NoteTemplate.libraryTopic, ...(data?.topic ?? [])],
    })
  }

  get data(): string {
    return base64Decode(this.resource.content?.[0]?.data ?? '')
  }

  set data(data: string) {
    this.resource.content = [{ data: base64Encode(data) }]
  }

  /**
   * Note labels set on this template, as `NoteLabel` reference strings. Stored
   * in `meta.tag` exactly like `CompositionNote.labels`, so the same vocabulary
   * and predicate (`NoteLabel.isValidReferenceString`) apply. These merge into a
   * note's labels when the template is instantiated or @-mention-inserted.
   */
  @Tag({
    system: NOTE_LABEL_TAG_SYSTEM,
    allowMultiple: true,
  })
  _labels?: Coding[]

  get labels(): NoteLabel['referenceString'][] {
    return readNoteLabelReferences(this._labels)
  }

  set labels(labels: NoteLabel['referenceString'][] | undefined) {
    this._labels = noteLabelTagCodings(labels)
  }

  /**
   * Hard-coded visit-note categories set on this template, as `system|code`
   * tokens. Stored as additional `Library.topic[]` codings alongside the `note`
   * topic that `isInstance`/`searchQuery` key on (preserved by the setter).
   * Categories merge 1:1 into a note's `Composition.category[]` when the
   * template is instantiated or @-mention-inserted.
   */
  get categories(): string[] {
    return readVisitNoteCategoryTokens(this.resource.topic)
  }

  set categories(tokens: string[] | undefined) {
    // The merge preserves the `note` library topic that `isInstance`/`searchQuery`
    // depend on; this setter owns ONLY the registry-category entries.
    this.resource.topic = mergeVisitNoteCategoryTokens(
      this.resource.topic,
      tokens
    )
  }

  static libraryTopic = codeableConcept({
    system: LIBRARY_TOPIC,
    code: 'note',
    display: 'Note',
  })

  static isInstance(value: unknown): value is NoteTemplate {
    return (
      isResource(value) &&
      isOfType(value, 'Library') &&
      hasCodeableConcept(value.topic, NoteTemplate.libraryTopic)
    )
  }

  static searchQuery(): QueryTypes {
    return {
      topic: this.libraryTopic,
      status: 'active',
      _sort: '-_lastUpdated',
    }
  }
}
